import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";
import { CostAssessmentRecordRow, DepreciatedRecordRow } from "../../types";
import { RecordOptions } from "./calculation";
import { normalizeRecords } from "./normalize";
import * as moment from "moment";

export interface SCurveOptions {
  kind: 's-curve'
  steepness: number,
  economicLife: number
}

export function sCurveMethod(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  sCurveOptions: SCurveOptions
) {
  const { economicLife, steepness } = sCurveOptions
  const { valuationDate, buildingConstructionDate, size } = recordOptions

  // Normalize
  const _records = normalizeRecords(records, recordOptions)
  const _steepness = steepness / 100
  const _valuationDate = moment(valuationDate)
  const _buildingConstructionDate = moment(buildingConstructionDate)
  const yearDiff = ModelAssumptionsUtil._yearsDiff(
    _valuationDate,
    _buildingConstructionDate
  )
  if (isNaN(yearDiff)) return null;
  const _mappedRecords: DepreciatedRecordRow[] = _records.map(item => {
    if (item.kind === 'header') {
      return {
        ...item,
        ...depreciatedCost(
          item, 
          _steepness,
          size,
          yearDiff,
          {...sCurveOptions, valuationDate, buildingConstructionDate}
        )
      }
    };
    return {
      ...item,
      ...depreciatedCost(
        item, 
        _steepness,
        size,
        yearDiff,
        {...sCurveOptions, valuationDate, buildingConstructionDate}
      )
    }
  })
  const hasNull = _mappedRecords.some(item => {
    if (item.kind === 'header') return false
    return item.depreciated_cost === null
  })
  if (hasNull) return null
  const _totalCost = _records.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.total_cost
    },
    0
  )
  const totalDepreciatedCost = _mappedRecords.reduce(
    (acc, item) => {
      if (item.kind === 'header') return acc
      if (item.is_removed) return acc
      return acc + item.depreciated_cost
    },
    0
  )

  return {
    records: _mappedRecords,
    graphValues: graphValues(economicLife, _totalCost, _steepness),
    totalCost: _totalCost,
    totalDepreciatedCost,
    yearDiff
  }
}

function depreciatedCost(
  {total_cost}: {total_cost: number},
  steepness: number,
  size: number,
  yearDiff: number,
  {valuationDate, buildingConstructionDate, economicLife}: SCurveOptions & {valuationDate: Date, buildingConstructionDate: Date}
): {
  depreciated_cost: number,
  depreciated_per_unit: number
} {
  const _sCurveFactor = sCurveFactor(yearDiff, economicLife, steepness)
  const _depreciatedValue = depreciatedValue(total_cost, _sCurveFactor)
  return {
    depreciated_cost: _depreciatedValue,
    depreciated_per_unit: _depreciatedValue / size
  }
}

function graphValues(
  economicLife: number,
  totalCost: number,
  steepness: number
): {age: number, depreciationFactor: number, depreciatedValue: number}[] {
  const values = []
  for (let i = 0; i <= economicLife; i++) {
    const _sCurveFactor = sCurveFactor(i, economicLife, steepness)
    values.push({
      age: i,
      depreciationFactor: _sCurveFactor,
      depreciatedValue: depreciatedValue(totalCost, _sCurveFactor)
    })
  }
  return values
}

function sCurveFactor(
  yearDiff: number,
  economicLife: number,
  steepness: number
): number {
  const exponent = -steepness * (yearDiff - (economicLife / 2))
  return 1 / (1 + Math.exp(exponent))
}

function depreciatedValue(
  cost: number,
  sCurveFactor: number
) {
  return cost * (1 - sCurveFactor)
}